import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Star } from '@/components/ui/Icons';
import { company } from '@/data/company';
import { Picture } from '@/components/ui/Picture';

type Props = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  imageSrc?: string;
  /** A handful of trust phrases shown beneath the CTAs (defaults to company.guarantees subset). */
  trust?: string[];
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

export function Hero({
  eyebrow,
  title,
  subtitle,
  imageSrc = '/images/hero/home.jpg',
  trust,
}: Props) {
  const trustPills = trust ?? company.guarantees.slice(0, 3);

  return (
    <section
      className="relative isolate flex min-h-svh w-full max-w-[100vw] items-center overflow-hidden bg-navy-950 pt-16 lg:min-h-dvh lg:pt-20"
      aria-label={title}
    >
      {/* Background photo */}
      <Picture
        src={imageSrc}
        alt=""
        className="absolute inset-0 -z-20 block"
        imgClassName="h-full w-full object-cover opacity-50 transition-transform duration-2000 ease-out animate-[zoom_18s_ease-out_forwards]"
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />

      {/* Layered overlays for depth */}
      <div className="absolute inset-0 -z-10 bg-linear-to-b from-navy-950/80 via-navy-900/65 to-navy-950" />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_15%_30%,rgba(184,145,70,0.18),transparent_55%),radial-gradient(circle_at_85%_85%,rgba(39,69,112,0.55),transparent_60%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 -z-10 h-1 gold-divider"
      />

      <div className="container-page relative flex w-full flex-col justify-center py-[clamp(2rem,6vh,4rem)]">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          className="max-w-3xl text-paper-0"
        >
          {eyebrow && (
            <motion.p
              variants={fadeUp}
              className="mb-[clamp(0.75rem,2vh,1.25rem)] inline-flex items-center gap-2 rounded-full border border-gold-400/40 bg-navy-900/50 px-3 py-1 text-[clamp(0.7rem,1.4vw,0.78rem)] font-semibold uppercase tracking-[0.22em] text-gold-300 backdrop-blur-xs"
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold-400 animate-[pulse_2s_ease-in-out_infinite]" />
              {eyebrow}
            </motion.p>
          )}

          <motion.h1
            variants={fadeUp}
            className="font-display font-semibold leading-[1.05] tracking-tighter text-[clamp(2.25rem,5.5vw+1rem,5rem)]"
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              variants={fadeUp}
              className="mt-[clamp(1rem,2.5vh,1.5rem)] max-w-2xl leading-relaxed text-paper-0/85 text-[clamp(1rem,1.4vw+0.4rem,1.25rem)]"
            >
              {subtitle}
            </motion.p>
          )}

          <motion.div
            variants={fadeUp}
            className="mt-[clamp(1.5rem,4vh,2.5rem)] flex flex-wrap items-center gap-3"
          >
            <Link to="/contact" className="btn-gold group text-base">
              Get a free quote
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a href={`tel:${company.phoneTel}`} className="btn-outline-gold text-base">
              <Phone className="h-4 w-4" />
              {company.phone}
            </a>
          </motion.div>

          <motion.ul
            variants={fadeUp}
            className="mt-[clamp(1.5rem,3.5vh,2.5rem)] flex flex-wrap items-center gap-x-5 gap-y-2.5 text-sm text-paper-0/80"
          >
            {trustPills.map((t) => (
              <li key={t} className="inline-flex items-center gap-2">
                <Star className="h-3.5 w-3.5 text-gold-400" />
                <span>{t}</span>
              </li>
            ))}
          </motion.ul>
        </motion.div>

        {/* Scroll cue - hidden on mobile to keep the fold clean */}
        <motion.a
          href="#below-hero"
          aria-label="Scroll to content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="pointer-events-none absolute inset-x-0 bottom-6 hidden justify-center text-paper-0/60 sm:flex"
        >
          <span className="pointer-events-auto inline-flex h-10 w-6 items-start justify-center rounded-full border border-paper-0/40 p-1.5">
            <span className="h-2 w-1 rounded-full bg-paper-0/80 animate-[scrollcue_1.6s_ease-in-out_infinite]" />
          </span>
        </motion.a>
      </div>

      <style>{`
        @keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } }
        @keyframes scrollcue {
          0% { transform: translateY(0); opacity: 0 }
          30% { opacity: 1 }
          80% { transform: translateY(8px); opacity: 0 }
          100% { transform: translateY(0); opacity: 0 }
        }
        @keyframes zoom {
          from { transform: scale(1.08) }
          to   { transform: scale(1.0) }
        }
      `}</style>

      <div id="below-hero" className="absolute bottom-0 h-1 w-full" />
    </section>
  );
}
